import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, Container, TextField, Typography, Paper } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { motion } from 'motion/react';
import { PublicSiteChrome, publicPageSx } from './site/PublicSiteChrome';
import DigipinDisclaimer from './site/DigipinDisclaimer';
import Footer from './Footer';
import { submitTierLead } from '../services/leadsService';
import { trackEvent } from '../services/analyticsService';
import { fetchPaymentConfig, startStudioCheckout } from '../services/paymentsService';

const TOKENS = {
  primary: '#1E40AF',
  secondary: '#3B82F6',
  accent: '#D97706',
  bgDark: '#0B132B',
  bgCard: 'rgba(15, 23, 42, 0.75)',
  border: 'rgba(219, 234, 254, 0.15)',
  textPrimary: '#F8FAFC',
  textSecondary: '#94A3B8',
  green: '#10B981',
};

type TierId = 'community' | 'studio' | 'enterprise';

const TIERS: {
  id: TierId;
  name: string;
  price: string;
  priceNote: string;
  blurb: string;
  features: string[];
  cta: string;
  ctaTo?: string;
  ctaHref?: string;
  highlight?: boolean;
  badge?: string;
  roadmapNote?: string;
}[] = [
  {
    id: 'community',
    name: 'Community',
    price: '₹0',
    priceNote: 'forever · every human',
    blurb:
      'Full WebGIS product free forever. DIGIPIN tools, draw, measure, share, plugins. Fair-use limits only — not a feature jail.',
    features: [
      'Full browser WebGIS (map, layers, export)',
      'Simply Map for non-technical users',
      'DIGIPIN on map & property cards',
      'Public share links (/m/…)',
      'QGIS plugins for personal & professional field work',
      'Fair-use rate limits (anti-abuse)',
    ],
    cta: 'Stay free — open map',
    ctaTo: '/map',
    highlight: true,
    badge: 'Free forever',
  },
  {
    id: 'studio',
    name: 'Studio',
    price: '₹999',
    priceNote: 'per month · paid via Razorpay (SIFA Geolabs)',
    blurb:
      'Same map product. Unlocks commercial DIGIPIN/API rights, higher quotas, and priority email support.',
    features: [
      'Everything in Community',
      'Commercial API license',
      'Higher DIGIPIN / API quotas',
      'Priority email support',
      'Pay online (Razorpay) under SIFA Geolabs',
    ],
    cta: 'Pay for Studio',
    // Scroll-handled in UI when id === studio
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    priceNote: 'orgs · gov · PSUs · design partners',
    blurb:
      'For municipalities, infrastructure programmes, and large survey firms that need volume, onboarding, and operational SLAs — partnered under SIFA Geolabs.',
    features: [
      'Everything in Studio (commercial path)',
      'Design-partner onboarding & rollout plan',
      'Bulk DIGIPIN / API volume discussion',
      'SLA & support channel conversation',
      'Named human contact (SIFA Geolabs)',
      'Admin ops: users, shares, payments visibility',
      'Roadmap influence: seats, roles, SSO, VPC',
    ],
    cta: 'Talk to Enterprise',
    ctaHref: 'mailto:support@geovera.in?subject=GeoVera%20Enterprise%20design%20partner',
    badge: 'Org scale',
    roadmapNote:
      'Seats, roles, audit export, SSO, and private/VPC deploy are design-partner priorities — not self-serve product yet.',
  },
];

const PricingPage = () => {
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactOrg, setContactOrg] = useState('');
  const [contactTier, setContactTier] = useState<'studio' | 'enterprise'>('studio');
  const [studioPlan, setStudioPlan] = useState<'studio_monthly' | 'studio_yearly'>(
    'studio_monthly'
  );
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [paying, setPaying] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formMessage, setFormMessage] = useState<string | null>(null);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [merchantLabel, setMerchantLabel] = useState('SIFA Geolabs');
  const [planPrices, setPlanPrices] = useState<{ monthly?: number; yearly?: number }>({});

  useEffect(() => {
    fetchPaymentConfig()
      .then((cfg) => {
        if (cfg.success) {
          setPaymentsEnabled(!!cfg.enabled);
          if (cfg.merchant) setMerchantLabel(cfg.merchant);
          const m = cfg.plans?.find((p) => p.code === 'studio_monthly');
          const y = cfg.plans?.find((p) => p.code === 'studio_yearly');
          setPlanPrices({
            monthly: m?.amountInr,
            yearly: y?.amountInr,
          });
        }
      })
      .catch(() => setPaymentsEnabled(false));
  }, []);

  const scrollToCheckout = (tier: 'studio' | 'enterprise' = 'studio') => {
    setContactTier(tier);
    document
      .getElementById('studio-checkout')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleStudioPay = async () => {
    setFormError(null);
    setFormMessage(null);
    if (!contactName.trim() || !contactEmail.trim()) {
      setFormError('Name and email are required for Razorpay receipt.');
      return;
    }
    setPaying(true);
    try {
      const result = await startStudioCheckout({
        planCode: studioPlan,
        name: contactName.trim(),
        email: contactEmail.trim(),
        organization: contactOrg || undefined,
      });
      setSent(true);
      setFormMessage(
        `Payment successful (${result.paymentId}). GeoVera Studio is active` +
          (result.activeUntil
            ? ` until ${new Date(result.activeUntil).toLocaleDateString('en-IN')}.`
            : '.') +
          ` Receipt from ${merchantLabel}.`
      );
      trackEvent('lead_submit', { tier: 'studio', paid: true, plan: studioPlan });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Payment failed or cancelled');
    } finally {
      setPaying(false);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Studio with payments: prefer pay path
    if (contactTier === 'studio' && paymentsEnabled) {
      await handleStudioPay();
      return;
    }
    setFormError(null);
    setFormMessage(null);
    setSending(true);
    try {
      const result = await submitTierLead({
        tier: contactTier,
        name: contactName,
        email: contactEmail,
        organization: contactOrg || undefined,
        message: 'I would like access / a design-partner conversation.',
        source: 'pricing_page',
      });
      setSent(true);
      setFormMessage(
        result.message || 'Thanks — we received your request and will follow up by email.'
      );
      trackEvent('lead_submit', { tier: contactTier, duplicate: !!result.duplicate });
    } catch (err) {
      const subject = encodeURIComponent(
        `GeoVera ${contactTier === 'studio' ? 'Studio' : 'Enterprise'} request — ${contactOrg || contactName}`
      );
      const body = encodeURIComponent(
        [
          `Name: ${contactName}`,
          `Email: ${contactEmail}`,
          `Organization: ${contactOrg || '—'}`,
          `Interest: ${contactTier}`,
          '',
          'Message: I would like access / a design-partner conversation.',
        ].join('\n')
      );
      setFormError(
        err instanceof Error
          ? `${err.message} Opening email as backup…`
          : 'Could not save request. Opening email as backup…'
      );
      window.location.href = `mailto:support@geovera.in?subject=${subject}&body=${body}`;
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ ...publicPageSx, bgcolor: TOKENS.bgDark, color: TOKENS.textPrimary }}>
      <PublicSiteChrome title="GeoVera" subtitle="Pricing" active="pricing" />

      <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 8 }, pb: { xs: 6, md: 10 } }}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <Box sx={{ mb: 2, textAlign: 'center', maxWidth: 720, mx: 'auto' }}>
            <Chip
              label="Enterprise pricing · Adoption first"
              size="small"
              sx={{
                mb: 1.5,
                bgcolor: 'rgba(16, 185, 129, 0.15)',
                color: TOKENS.green,
                fontWeight: 700,
                fontSize: 11,
              }}
            />
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 800,
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
                mb: 1.5,
              }}
            >
              Transparent tiers for people and organisations.
              <Box component="span" sx={{ color: TOKENS.accent, display: 'block' }}>
                Full product free. Pay only for commercial scale.
              </Box>
            </Typography>
            <Typography
              sx={{ color: TOKENS.textSecondary, fontSize: { xs: 15, md: 17 }, lineHeight: 1.6 }}
            >
              Community is the full WebGIS product — free forever. Studio unlocks commercial API
              rights and higher fair-use. Enterprise is a partnered programme for orgs that need
              SLAs, bulk DIGIPIN, and onboarding under{' '}
              <strong style={{ color: TOKENS.textPrimary }}>SIFA Geolabs</strong>.
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 720, mx: 'auto', mb: 5 }}>
            <DigipinDisclaimer variant="banner" />
          </Box>
        </motion.div>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          {TIERS.map((tier, i) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 * i, duration: 0.4 }}
              style={{ height: '100%' }}
            >
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: TOKENS.bgCard,
                  border: tier.highlight
                    ? '1px solid rgba(16, 185, 129, 0.45)'
                    : `1px solid ${TOKENS.border}`,
                  borderRadius: 3,
                  boxShadow: tier.highlight ? '0 0 40px rgba(16, 185, 129, 0.12)' : 'none',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {tier.badge && (
                  <Chip
                    label={tier.badge}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      bgcolor:
                        tier.id === 'enterprise'
                          ? 'rgba(217, 119, 6, 0.2)'
                          : 'rgba(16, 185, 129, 0.2)',
                      color: tier.id === 'enterprise' ? '#FBBF24' : TOKENS.green,
                      fontWeight: 700,
                      fontSize: 10,
                    }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: TOKENS.secondary,
                    letterSpacing: '0.06em',
                  }}
                >
                  {tier.name.toUpperCase()}
                </Typography>
                <Typography
                  sx={{ mt: 1, mb: 0.5, fontSize: 36, fontWeight: 800, letterSpacing: '-0.03em' }}
                >
                  {tier.price}
                </Typography>
                <Typography sx={{ fontSize: 12, color: TOKENS.textSecondary, mb: 2 }}>
                  {tier.priceNote}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: TOKENS.textSecondary,
                    lineHeight: 1.55,
                    mb: 2.5,
                    flexGrow: 0,
                  }}
                >
                  {tier.blurb}
                </Typography>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, mb: 3, flexGrow: 1 }}
                >
                  {tier.features.map((f) => (
                    <Box key={f} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <CheckCircleOutlinedIcon
                        sx={{ fontSize: 18, color: TOKENS.green, mt: 0.15 }}
                      />
                      <Typography
                        sx={{ fontSize: 13, color: TOKENS.textPrimary, lineHeight: 1.45 }}
                      >
                        {f}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {tier.roadmapNote && (
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: '#64748B',
                      fontStyle: 'italic',
                      mb: 2,
                      lineHeight: 1.45,
                    }}
                  >
                    Roadmap: {tier.roadmapNote}
                  </Typography>
                )}
                {tier.ctaTo ? (
                  <Button
                    component={RouterLink}
                    to={tier.ctaTo}
                    variant="contained"
                    fullWidth
                    endIcon={<ArrowOutwardIcon />}
                    sx={{
                      mt: 'auto',
                      py: 1.25,
                      fontWeight: 700,
                      textTransform: 'none',
                      bgcolor: TOKENS.green,
                      color: '#042f1a',
                      '&:hover': { bgcolor: '#34D399' },
                    }}
                  >
                    {tier.cta}
                  </Button>
                ) : tier.id === 'studio' ? (
                  <Button
                    variant="contained"
                    fullWidth
                    endIcon={<ArrowOutwardIcon />}
                    onClick={() => scrollToCheckout('studio')}
                    sx={{
                      mt: 'auto',
                      py: 1.25,
                      fontWeight: 700,
                      textTransform: 'none',
                      bgcolor: TOKENS.accent,
                      '&:hover': { bgcolor: '#B45309' },
                    }}
                  >
                    {paymentsEnabled
                      ? `Pay Studio · ₹${planPrices.monthly ?? 999}/mo`
                      : 'Get Studio'}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    fullWidth
                    endIcon={<ArrowOutwardIcon />}
                    onClick={() => scrollToCheckout('enterprise')}
                    sx={{
                      mt: 'auto',
                      py: 1.25,
                      fontWeight: 700,
                      textTransform: 'none',
                      borderColor: TOKENS.border,
                      color: TOKENS.textPrimary,
                      '&:hover': {
                        borderColor: TOKENS.secondary,
                        bgcolor: 'rgba(59,130,246,0.08)',
                      },
                    }}
                  >
                    {tier.cta}
                  </Button>
                )}
              </Paper>
            </motion.div>
          ))}
        </Box>

        {/* Contact / request form */}
        <Paper
          id="studio-checkout"
          component="form"
          onSubmit={handleContactSubmit}
          elevation={0}
          sx={{
            mt: 6,
            p: { xs: 3, md: 4 },
            bgcolor: TOKENS.bgCard,
            border: `1px solid ${TOKENS.border}`,
            borderRadius: 3,
            maxWidth: 640,
            mx: 'auto',
          }}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 800, mb: 0.5 }}>
            {contactTier === 'studio' && paymentsEnabled
              ? 'Pay for GeoVera Studio'
              : 'Request Studio or Enterprise'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: TOKENS.textSecondary, mb: 1.5 }}>
            {contactTier === 'studio' && paymentsEnabled
              ? `Secure checkout via Razorpay. Merchant: ${merchantLabel}. Product: GeoVera Studio.`
              : 'Saved to our team queue (and email notification). Enterprise remains design-partner sales.'}
          </Typography>
          {contactTier === 'studio' && paymentsEnabled && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`Monthly · ₹${planPrices.monthly ?? 999}`}
                onClick={() => setStudioPlan('studio_monthly')}
                sx={{
                  fontWeight: 700,
                  cursor: 'pointer',
                  bgcolor: studioPlan === 'studio_monthly' ? 'rgba(217,119,6,0.25)' : 'transparent',
                  color: TOKENS.textPrimary,
                  border: `1px solid ${studioPlan === 'studio_monthly' ? TOKENS.accent : TOKENS.border}`,
                }}
              />
              <Chip
                label={`Yearly · ₹${planPrices.yearly ?? 9999}`}
                onClick={() => setStudioPlan('studio_yearly')}
                sx={{
                  fontWeight: 700,
                  cursor: 'pointer',
                  bgcolor: studioPlan === 'studio_yearly' ? 'rgba(217,119,6,0.25)' : 'transparent',
                  color: TOKENS.textPrimary,
                  border: `1px solid ${studioPlan === 'studio_yearly' ? TOKENS.accent : TOKENS.border}`,
                }}
              />
            </Box>
          )}
          {formMessage && (
            <Typography sx={{ fontSize: 13, color: TOKENS.green, mb: 2, fontWeight: 600 }}>
              {formMessage}
            </Typography>
          )}
          {formError && (
            <Typography sx={{ fontSize: 13, color: '#FBBF24', mb: 2 }}>{formError}</Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
              }}
            >
              <TextField
                required
                label="Name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                fullWidth
                size="small"
                sx={fieldSx}
              />
              <TextField
                required
                type="email"
                label="Email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                fullWidth
                size="small"
                sx={fieldSx}
              />
            </Box>
            <TextField
              label="Organization"
              value={contactOrg}
              onChange={(e) => setContactOrg(e.target.value)}
              fullWidth
              size="small"
              sx={fieldSx}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              {(['studio', 'enterprise'] as const).map((t) => (
                <Chip
                  key={t}
                  label={t === 'studio' ? 'Studio' : 'Enterprise'}
                  onClick={() => setContactTier(t)}
                  sx={{
                    fontWeight: 700,
                    cursor: 'pointer',
                    bgcolor: contactTier === t ? 'rgba(59,130,246,0.25)' : 'transparent',
                    color: TOKENS.textPrimary,
                    border: `1px solid ${contactTier === t ? TOKENS.secondary : TOKENS.border}`,
                  }}
                />
              ))}
            </Box>
            <Button
              type="submit"
              variant="contained"
              disabled={sending || paying || (sent && !formError)}
              sx={{
                alignSelf: 'flex-start',
                fontWeight: 700,
                textTransform: 'none',
                bgcolor: TOKENS.accent,
                '&:hover': { bgcolor: '#B45309' },
              }}
            >
              {paying
                ? 'Opening Razorpay…'
                : sending
                  ? 'Sending…'
                  : sent && !formError
                    ? contactTier === 'studio' && paymentsEnabled
                      ? 'Payment complete'
                      : 'Request received'
                    : contactTier === 'studio' && paymentsEnabled
                      ? `Pay with Razorpay · ${merchantLabel}`
                      : 'Send request'}
            </Button>
            {contactTier === 'studio' && paymentsEnabled && (
              <Typography sx={{ fontSize: 11, color: '#64748B' }}>
                You will see <strong>{merchantLabel}</strong> on the Razorpay payment screen.
              </Typography>
            )}
          </Box>
        </Paper>

        <Typography
          sx={{
            mt: 4,
            textAlign: 'center',
            fontSize: 13,
            color: TOKENS.textSecondary,
            maxWidth: 560,
            mx: 'auto',
          }}
        >
          License: browser WebGIS is free for humans, including professional individuals. Studio is
          for high-volume commercial API and redistribution of the API — not for opening the map.
        </Typography>
      </Container>

      <Footer />
    </Box>
  );
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#F8FAFC',
    '& fieldset': { borderColor: 'rgba(219, 234, 254, 0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(59, 130, 246, 0.5)' },
  },
  '& .MuiInputBase-input': { color: '#F8FAFC' },
} as const;

export default PricingPage;
